/**
 * กติกาเดียวของ "ปุ่มปิดงวดกดได้ไหม" — ใช้ร่วมกันระหว่างหน้าจอกับเทส
 *
 * 🚨 บั๊กที่ไฟล์นี้เกิดมาเพื่อกัน (เจอจริง 8 ก.ย. 2569 · TN ริชเชสค้าง 3 งวด):
 * backend แบ่งของค้างเป็น 2 ชั้น — `hardBlock` (ปิดไม่ได้จริง) กับแดงธรรมดา
 * (ปิดได้ถ้ายืนยันพร้อมเหตุผล → POST ตอบ 409 needs_acknowledge แล้วเด้งกล่อง
 * ให้พิมพ์เหตุผล) แต่หน้าจอเดิมปิดปุ่มเมื่อ `!can_close` ซึ่งเป็นเท็จทันทีที่มี
 * ใบลารออนุมัติแค่ใบเดียว → 409 ไม่มีวันถูกยิง กล่องเหตุผลไม่มีวันโผล่ =
 * **ทางออกที่ backend เตรียมไว้ ถูกปุ่มที่ปิดตัวเองบังไว้ทั้งเส้น**
 *
 * ⭐ ด่านฝั่งจอห้ามเข้มกว่าด่านฝั่งเซิร์ฟเวอร์ — ถ้าเซิร์ฟเวอร์ยอมให้ทำ
 * (ต่อให้ต้องมีเงื่อนไข) หน้าจอต้องมีทางเดินไปถึงเงื่อนไขนั้นเสมอ
 */

/** สถานะรวมของงวดตาม contract หน้า "ปิดเดือนนี้" */
export type CloseRunStatus = 'none' | 'draft' | 'previewed' | 'approved' | 'locked' | 'sent';

export interface CloseGateInput {
  /** สถานะงวดจาก preflight */
  runStatus: CloseRunStatus;
  /** can_close จาก backend = ปิดได้สะอาด ไม่ต้องให้เหตุผล */
  canClose: boolean;
  /** readiness.hardBlockCount จาก backend = จำนวนรายการที่ override ไม่ได้ */
  hardBlockCount: number;
  /** กำลังยิง POST อยู่ (กันกดซ้ำ) */
  submitting?: boolean;
}

export type CloseGateMode =
  /** ปิดแล้ว ส่งสลิปไม่ครบ → ปุ่มกลายเป็น "ส่งสลิป" */
  | 'resend'
  /** ปิด+ส่งสลิปครบแล้ว → ไม่มีปุ่ม */
  | 'done'
  /** มี hardBlock → กดไม่ได้จริง ต้องไปแก้ต้นเหตุก่อน */
  | 'blocked'
  /** ปิดได้ แต่ระบบจะขอเหตุผลก่อน (แดงที่ override ได้) */
  | 'needs_reason'
  /** ปิดได้สะอาด */
  | 'ready';

export interface CloseGate {
  mode: CloseGateMode;
  /** ปุ่มหลักถูก disable ไหม */
  disabled: boolean;
  /** จะต้องเจอกล่องขอเหตุผลต่อจากนี้ไหม — ใช้เตือนล่วงหน้าใน ConsequencePreview */
  willAskReason: boolean;
}

/**
 * ตัดสินสถานะปุ่มปิดงวดจากข้อมูล preflight.
 *
 * กติกา: **เฉพาะ hardBlock เท่านั้นที่ปิดปุ่ม** — ของค้างที่ backend ยอมให้
 * override ต้องปล่อยให้กดได้ แล้วไปจบที่กล่องขอเหตุผล ไม่ใช่ตายตั้งแต่ปุ่ม
 */
export function evaluateCloseGate(input: CloseGateInput): CloseGate {
  const { runStatus, canClose, hardBlockCount, submitting = false } = input;

  if (runStatus === 'sent') {
    return { mode: 'done', disabled: true, willAskReason: false };
  }

  if (runStatus === 'locked') {
    // ล็อกแล้วแต่สลิปยังไม่ครบ — ส่งซ้ำได้เสมอ ของค้างไม่เกี่ยวแล้ว
    return { mode: 'resend', disabled: submitting, willAskReason: false };
  }

  if (hardBlockCount > 0) {
    return { mode: 'blocked', disabled: true, willAskReason: false };
  }

  if (!canClose) {
    return { mode: 'needs_reason', disabled: submitting, willAskReason: true };
  }

  return { mode: 'ready', disabled: submitting, willAskReason: false };
}

// ─────────────────────────────────────────────────────────────
// คำยืนยัน (acknowledge) ต้องผูกกับบริบทที่ยิงไป ไม่ใช่บริบทที่จอถืออยู่
// ─────────────────────────────────────────────────────────────

/** งวด + บริษัท ของคำขอที่ทำให้กล่องขอเหตุผลเปิดขึ้นมา */
export interface AckContext {
  period: string;
  tenantId: string | null;
}

export type AckTarget =
  | { ok: true; period: string }
  /** กล่องเปิดค้างแต่ไม่มีบริบท (รีเฟรช/สถานะหาย) — ต้องเริ่มใหม่ */
  | { ok: false; reason: 'no_context' }
  /** สลับบริษัทระหว่างทาง — เหตุผลของบริษัทหนึ่งห้ามใช้ปิดงวดอีกบริษัท */
  | { ok: false; reason: 'tenant_changed' };

/**
 * ตัดสินว่าคำยืนยันนี้ควรถูกส่งไปที่งวดไหน — หรือควรถูกทิ้ง
 *
 * 🚨 บั๊กที่ฟังก์ชันนี้เกิดมาเพื่อกัน (astra จำลองได้จริง 8 ก.ย. 2569):
 *   · ยืนยันงวด ส.ค. → ระหว่าง POST ช้า ผู้ใช้เปลี่ยนดรอปดาวน์เป็น ก.ค.
 *     → 409 ของ ส.ค. กลับมาเปิดกล่อง → พิมพ์เหตุผล → ระบบ **ปิด ก.ค.** แทน
 *   · แบบเดียวกันกับการสลับบริษัท: JWT เปลี่ยนทันทีที่สลับ แต่กล่องไม่มี
 *     บริษัทกำกับ → เหตุผลของบริษัท A ไปปิดงวดของบริษัท B
 *
 * ⭐ หลักทั่วไป: **ทุกคำยืนยันที่ทำ action ย้อนยาก ต้องพก "สิ่งที่ยืนยัน" ไป
 * ด้วยตัวเอง** ห้ามอ่านค่าจาก state ตอนกดยืนยัน เพราะ state เปลี่ยนได้ระหว่าง
 * ที่คนกำลังอ่านกล่อง
 */
export function resolveAckTarget(ctx: AckContext | null, currentTenantId: string | null): AckTarget {
  if (!ctx) return { ok: false, reason: 'no_context' };
  if (ctx.tenantId !== currentTenantId) return { ok: false, reason: 'tenant_changed' };
  return { ok: true, period: ctx.period };
}
